/**
 * DeviceSelectorModal - Audio input device selection modal
 */
import React, { memo } from 'react';
import { Button } from './ui/button';

const DeviceSelectorModal = memo(({
  isVisible,
  devices,
  selectedDeviceId,
  onClose,
  onSelectDevice,
}) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="device-selector-modal">
      <div className="bg-gradient-to-br from-purple-900 to-gray-900 p-6 rounded-xl max-w-md w-full mx-4 border border-purple-500/30 max-h-[80vh] overflow-y-auto">
        <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
          Select Audio Input Device
        </h3>

        <p className="text-white/70 text-sm mb-4">
          Choose your virtual loopback device (BlackHole, VB-Cable, etc.)
        </p>

        <div className="space-y-3 mb-6">
          {devices.length === 0 ? (
            <div className="bg-yellow-500/20 border border-yellow-400/30 p-4 rounded-lg">
              <div className="text-yellow-200 text-sm">
                <strong>No devices found</strong>
                <div className="mt-2 text-xs">
                  Make sure you&rsquo;ve:
                  <ul className="list-disc list-inside mt-1 ml-2">
                    <li>Granted microphone permission</li>
                    <li>Installed a loopback device</li>
                    <li>Refreshed the page after installation</li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <>
              {devices.map((device, index) => (
                <button
                  key={device.deviceId}
                  onClick={() => onSelectDevice(device.deviceId)}
                  className="w-full text-left p-3 bg-white/10 hover:bg-white/20 rounded-lg border border-purple-400/30 transition-colors"
                  data-testid={`device-option-${index}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="text-white font-medium">
                        {device.label || `Audio Input Device ${index + 1}`}
                      </div>
                      {!device.label && (
                        <div className="text-xs text-white/50 mt-1">
                          ID: {device.deviceId.substring(0, 20)}...
                        </div>
                      )}
                    </div>
                    {device.deviceId === selectedDeviceId && (
                      <div className="text-xs text-green-400 font-bold ml-2">✓</div>
                    )}
                  </div>
                  {device.deviceId === selectedDeviceId && (
                    <div className="text-xs text-green-400 mt-2 bg-green-500/10 px-2 py-1 rounded">
                      Currently active
                    </div>
                  )}
                </button>
              ))}

              <div className="bg-blue-500/20 border border-blue-400/30 p-3 rounded-lg mt-4">
                <div className="text-blue-200 text-xs">
                  <strong>Tip:</strong> Look for devices named &ldquo;BlackHole&rdquo;, &ldquo;VB-Cable&rdquo;, or similar virtual audio devices.
                </div>
              </div>
            </>
          )}
        </div>

        <Button
          onClick={onClose}
          className="w-full bg-gray-600 hover:bg-gray-700"
          data-testid="device-selector-close-btn"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
});

DeviceSelectorModal.displayName = 'DeviceSelectorModal';

export default DeviceSelectorModal;
